import { CircularProgress, makeStyles, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { debounce, find } from "lodash";
import React from "react";
import { useCallback, useEffect, useState } from "react";
import { useDataProvider, useNotify } from "react-admin";

interface InvoiceInputProps {
  mbcCustomerNo: string;
  creditNoteAmount: number;
  label: string;
  setSelectedInvoice: (invoice: ICustomerLedger) => void;
}

const useStyles = makeStyles({
  root: {
    marginTop: "8px",
    paddingBottom: "3px",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#EAEAEA",
      },
      "&:hover fieldset": {
        borderColor: "#EAEAEA",
      },
    },
  },
});

export default function InvoiceInput({
  mbcCustomerNo,
  label,
  setSelectedInvoice,
  creditNoteAmount,
}: InvoiceInputProps) {
  const [customerLedgers, setCustomerLedgers] = useState<ICustomerLedger[]>([]);
  const [optionValues, setOptionValues] = useState<string[]>([]);
  const [value, setValue] = useState<string>("");
  const provider = useDataProvider();
  const notify = useNotify();
  const [loading, setLoading] = useState<boolean>(false);

  const classes = useStyles();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const callInvoiceListApi = useCallback(
    debounce((searchVal: string, mbcCustomerId: string) => {
      provider
        .customGetList("customerLedgers", {
          filter: {
            CustomerNo: mbcCustomerId,
            DocumentType: "Invoice",
            InvoiceNo: searchVal,
            Open: "true",
            MinimumAmount: creditNoteAmount,
          },
        })
        .then(({ data }: any) => {
          if (data.values.length > 0) {
            setCustomerLedgers(data.values);
            setOptionValues(
              data.values.map(
                (item: any) =>
                  `${item.no} - ${item.description} ($ ${item.remainingAmountLcy})`
              )
            );
            setLoading(false);
          } else {
            setCustomerLedgers([]);
            setOptionValues([]);
            setLoading(false);
          }
        })
        .catch((error: any) => {
          notify(`Error from MBC : ${error.message}`, "error");
          setCustomerLedgers([]);
          setOptionValues([]);
          setLoading(false);
        });
    }, 1000),
    []
  );

  useEffect(() => {
    setLoading(true);
    callInvoiceListApi(value, mbcCustomerNo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Autocomplete
      className={classes.root}
      options={optionValues}
      onChange={(event, newValue, reason) => {
        if (reason === "select-option") {
          const splitedValue = newValue.split(" ");

          console.log([splitedValue[splitedValue.length - 2]]);
          setValue(splitedValue[0]);

          const selectedInvoice = find(customerLedgers, [
            "no",
            splitedValue[0],
          ]);
          setSelectedInvoice(selectedInvoice);
        }

        if (reason === "clear") {
          setValue("");
          setSelectedInvoice(null);
        }
      }}
      freeSolo
      value={value}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={label}
          InputProps={{
            ...params.InputProps,
            type: "search",
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
}
